import React from "react";
import { addDays, addMonths, differenceInCalendarDays, endOfMonth, startOfMonth } from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

const iso10 = (d) => d.toDateString();

// Work week runs Thursday -> Wednesday
export const workWeekOf = (d) => {
    const start = addDays(d, -((d.getDay() - 4 + 7) % 7));
    return { from: start, to: addDays(start, 6) };
};

const Btn = ({ children, onClick, testId, active }) => (
    <button onClick={onClick} data-testid={testId}
        className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1 ${active ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 text-neutral-700 hover:bg-neutral-50"}`}>
        {children}
    </button>
);

export function DateSelector({ mode, setMode, range, setRange, markedDays, month, setMonth }) {
    const to = range.to || range.from;
    const span = differenceInCalendarDays(to, range.from) + 1;
    const isFullMonth = mode === "range" && range.from.getDate() === 1 && iso10(to) === iso10(endOfMonth(range.from));
    const shiftPeriod = (dir) => {
        if (isFullMonth) {
            const from = startOfMonth(addMonths(range.from, dir));
            setRange({ from, to: endOfMonth(from) });
            setMonth(from);
        } else shift((d) => addDays(d, mode === "single" ? dir : dir * span));
    };
    const shift = (fn) => {
        const len = span - 1;
        const from = fn(range.from);
        const next = { from, to: mode === "single" ? from : addDays(from, len) };
        setRange(next);
        setMonth(next.from);
    };
    const preset = (r) => { setMode("range"); setRange(r); setMonth(r.from); };
    const marked = markedDays.map((s) => new Date(`${s}T00:00:00`));

    return (
        <div className="bg-white border border-neutral-200 rounded-md p-4 flex flex-col md:flex-row gap-6" data-testid="tm-date-selector">
            <Calendar
                mode={mode}
                selected={mode === "single" ? range.from : range}
                onSelect={(v, clicked) => {
                    if (mode === "single") { if (v) setRange({ from: v, to: v }); return; }
                    if (range.to || !v) setRange({ from: clicked, to: undefined });
                    else setRange({ from: v.from, to: v.to });
                }}
                month={month}
                onMonthChange={setMonth}
                locale={it}
                weekStartsOn={4}
                captionLayout="dropdown-buttons"
                fromYear={2000}
                toYear={new Date().getFullYear() + 1}
                modifiers={{ hasData: marked }}
                modifiersClassNames={{ hasData: "tm-has-data" }}
                classNames={{
                    caption_dropdowns: "flex gap-2",
                    dropdown: "absolute inset-0 w-full opacity-0 cursor-pointer",
                    dropdown_month: "relative inline-flex items-center text-sm font-medium capitalize",
                    dropdown_year: "relative inline-flex items-center text-sm font-medium",
                    caption_label: "text-sm font-medium capitalize inline-flex items-center gap-0.5",
                    vhidden: "hidden",
                    day_range_middle: "aria-selected:bg-[#FFF7B0] aria-selected:text-neutral-900",
                }}
                data-testid="tm-calendar"
            />
            <div className="flex-1 space-y-4">
                <div className="flex gap-2" data-testid="tm-mode-toggle">
                    <Btn testId="tm-mode-single" active={mode === "single"} onClick={() => { setMode("single"); setRange({ from: range.from, to: range.from }); }}>Giorno</Btn>
                    <Btn testId="tm-mode-range" active={mode === "range"} onClick={() => { setMode("range"); setRange({ from: range.from, to: undefined }); }}>Intervallo</Btn>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Btn testId="tm-prev" onClick={() => shiftPeriod(-1)}><ChevronLeft size={12} /> Precedente</Btn>
                    <Btn testId="tm-next" onClick={() => shiftPeriod(1)}>Successivo <ChevronRight size={12} /></Btn>
                    <Btn testId="tm-today" onClick={() => { const t = new Date(); setMode("single"); setRange({ from: t, to: t }); setMonth(t); }}>Oggi</Btn>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Btn testId="tm-preset-week" onClick={() => preset(workWeekOf(range.from))}>Settimana (gio–mer)</Btn>
                </div>
                <div className="text-xs text-neutral-500 flex items-center gap-2">
                    {mode === "range" && !range.to && <span className="text-neutral-700" data-testid="tm-range-hint">Clicca il giorno di fine intervallo ·</span>}
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFE500] ring-1 ring-neutral-400" /> giorni con almeno un contenuto
                </div>
            </div>
        </div>
    );
}
